import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { ZapierZap } from '@sendhub/integrations/api-client-typescript-axios'
import { RootState } from '../root'
import { flatMap } from 'lodash'
import { AxiosRequestConfig } from 'axios'
import { selectZapierServiceAccountByOwnerId } from '../service-account/service-accounts.slice'
import { createZapierApiClient } from '../../services/zapier-api'
export const zapierApi = createZapierApiClient()

// We have to vary the query params a bit to avoid some caching issues...
//  I've found that higher limits (eg. 50 to 100) sometimes cause zap.steps[x].app to be null
const MAX_ZAPIER_ZAPS_PAGE_SIZE = 20
const MIN_ZAPIER_ZAPS_PAGE_SIZE = 5

export const ZAPIER_ZAPS_FEATURE_KEY = 'zapierZaps'

type ZapierZapWithOwnerId = ZapierZap & {
  ownerId: number
}

export interface ZapierZapsState extends EntityState<Readonly<ZapierZapWithOwnerId>> {
  loadingStatus: 'not loaded' | 'loading' | 'loaded' | 'error'
  error?: string | null
  zapsPageSize: number
}

export const zapierZapsAdapter = createEntityAdapter<Readonly<ZapierZapWithOwnerId>>()

/**
 * Export an effect using createAsyncThunk from
 * the Redux Toolkit: https://redux-toolkit.js.org/api/createAsyncThunk
 *
 * e.g.
 * ```
 * import React, { useEffect } from 'react';
 * import { useDispatch } from 'react-redux';
 *
 * // ...
 *
 * const dispatch = useDispatch();
 * useEffect(() => {
 *   dispatch(fetchZapierZaps())
 * }, [dispatch]);
 * ```
 */
export const fetchZapierZaps = createAsyncThunk('zapierZaps/fetchStatus', async ({ ownerId }: { ownerId: number }, thunkAPI) => {
  const state = thunkAPI.getState() as RootState
  const zapierServiceAccount = selectZapierServiceAccountByOwnerId(ownerId)(state)

  if (!zapierServiceAccount) throw new Error(`No Zapier ServiceAccount found!`)
  if (!('accessToken' in zapierServiceAccount.credentials)) throw new Error(`accessToken missing in Zapier ServiceAccount!`)

  const pageSize = state.zapierZaps.zapsPageSize
  const axiosConfig = {
    headers: {
      authorization: `Bearer ${zapierServiceAccount.credentials.accessToken}`,
    },
    withCredentials: true,
    params: { ts: new Date().toISOString() },
  } as AxiosRequestConfig
  console.log('zaps pageSize:', pageSize)
  return zapierApi.listZapierZaps(pageSize, 0, undefined, axiosConfig).then((firstResp) => {
    const firstPage = firstResp.data.objects

    const totalPages = Math.ceil(firstResp.data.count / pageSize)
    const remainingPages = totalPages - 1
    return Promise.all(
      [...Array(remainingPages).keys()].map((currentPage) =>
        zapierApi
          .listZapierZaps(pageSize, pageSize * (currentPage + 1) /** skip page 0 */, undefined, axiosConfig)
          .then((resp) => resp.data.objects)
      )
    ).then((otherPages) => {
      return [...firstPage, ...flatMap(otherPages)].map((zap): ZapierZapWithOwnerId => ({ ...zap, ownerId }))
    })
  })
})

export const initialZapierZapsState: ZapierZapsState = zapierZapsAdapter.getInitialState({
  loadingStatus: 'not loaded',
  error: null,
  zapsPageSize: MAX_ZAPIER_ZAPS_PAGE_SIZE,
})

export const zapierZapsSlice = createSlice({
  name: ZAPIER_ZAPS_FEATURE_KEY,
  initialState: initialZapierZapsState,
  reducers: {
    add: zapierZapsAdapter.addOne,
    setMany: zapierZapsAdapter.setMany,
    remove: zapierZapsAdapter.removeOne,
    // ...
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchZapierZaps.pending, (state: ZapierZapsState) => {
        state.loadingStatus = 'loading'
      })
      .addCase(fetchZapierZaps.fulfilled, (state: ZapierZapsState, action) => {
        zapierZapsAdapter.setAll(state, action.payload)
        state.loadingStatus = 'loaded'

        // Vary the query params a bit to avoid some caching issues...
        let newPageSize = state.zapsPageSize - 1
        if (newPageSize < MIN_ZAPIER_ZAPS_PAGE_SIZE) newPageSize = MAX_ZAPIER_ZAPS_PAGE_SIZE
        state.zapsPageSize = newPageSize
      })
      .addCase(fetchZapierZaps.rejected, (state: ZapierZapsState, action) => {
        state.loadingStatus = 'error'
        state.error = action.error.message
      })
  },
})

/*
 * Export reducer for store configuration.
 */
export const zapierZapsReducer = zapierZapsSlice.reducer

/*
 * Export action creators to be dispatched. For use with the `useDispatch` hook.
 *
 * e.g.
 * ```
 * import React, { useEffect } from 'react';
 * import { useDispatch } from 'react-redux';
 *
 * // ...
 *
 * const dispatch = useDispatch();
 * useEffect(() => {
 *   dispatch(zapierZapsActions.add({ id: 1 }))
 * }, [dispatch]);
 * ```
 *
 * See: https://react-redux.js.org/next/api/hooks#usedispatch
 */
export const zapierZapsActions = zapierZapsSlice.actions

/*
 * Export selectors to query state. For use with the `useSelector` hook.
 *
 * e.g.
 * ```
 * import { useSelector } from 'react-redux';
 *
 * // ...
 *
 * const entities = useSelector(selectAllZapierZaps);
 * ```
 *
 * See: https://react-redux.js.org/next/api/hooks#useselector
 */
const { selectAll, selectEntities } = zapierZapsAdapter.getSelectors()

export const getZapierZapsState = (rootState: RootState): ZapierZapsState => rootState[ZAPIER_ZAPS_FEATURE_KEY]

export const selectAllZapierZaps = createSelector(getZapierZapsState, selectAll)

export const selectAllZapierZapsByOwnerId = (ownerId: number) =>
  createSelector(getZapierZapsState, (state): Readonly<ZapierZapWithOwnerId>[] => selectAll(state).filter((zap) => zap.ownerId === ownerId))

export const selectZapierZapsEntities = createSelector(getZapierZapsState, selectEntities)
export const selectZapierZapsLoadingStatus = createSelector(getZapierZapsState, (state) => state.loadingStatus)
export const selectZapierZapsError = createSelector(getZapierZapsState, (state) => state.error)
